/**
 * Guardado de los servicios con precio de un cliente en edición, como diferencia contra lo que vio el
 * usuario.
 *
 * La línea base son las filas activas que el wizard precargó. La RPC `guardar_servicios_cliente` aplica
 * solo lo que el usuario cambió respecto de esa base, en una transacción: da de baja las filas que
 * quitó, da de alta los servicios que agregó, y un cambio de precio, frecuencia o mes de inicio da de
 * baja la fila vieja y da de alta una nueva, para conservar el historial. Lo que no estaba en la base,
 * por ejemplo un servicio que otro usuario agregó mientras tanto, no se toca.
 *
 * Regla de conflicto, que aplica la RPC con las filas bloqueadas: hay conflicto si una fila que el
 * guardado tiene que dar de baja ya no está activa o cambió de condiciones, o si un servicio que el
 * usuario agrega ya está activo para el cliente. Con un conflicto no se escribe ningún servicio y la RPC
 * levanta `CODIGO_CONFLICTO_SERVICIOS` con los `servicio_id` que chocaron.
 */
import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type { ServicioAlta } from '@/services/clientesService'

type FilaServicioCliente = Database['public']['Tables']['cliente_servicio_precios']['Row']

/** Una fila activa de `cliente_servicio_precios` tal como se leyó. */
export type ServicioLeido = Pick<FilaServicioCliente, 'id' | 'servicio_id' | 'precio' | 'frecuencia' | 'mes_inicio'>

/** SQLSTATE con el que `guardar_servicios_cliente` avisa un conflicto. El DETAIL trae los `servicio_id`. */
export const CODIGO_CONFLICTO_SERVICIOS = 'BHS01'

export type ResultadoGuardarServicios = { conflicto: false } | { conflicto: true; servicioIds: string[] }

type ErrorRpc = { code?: string; details?: string | null }

// La RPC no está en los tipos generados de Database: firma manual hasta la próxima regeneración.
type RpcGuardarServicios = (
  fn: 'guardar_servicios_cliente',
  args: { p_cliente_id: string; p_base: ServicioLeido[]; p_final: ServicioAlta[] },
) => PromiseLike<{ error: ErrorRpc | null }>

const servicioIdsDelDetalle = (detalle: string | null | undefined): string[] => {
  try {
    const ids: unknown = JSON.parse(detalle ?? '')
    return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []
  } catch {
    return []
  }
}

/**
 * Lleva los servicios del cliente de `base` a `final`. Con conflicto no escribe nada y devuelve los
 * `servicio_id` que chocaron. Cualquier otro error, incluido uno de permisos, se lanza.
 */
export const guardarServiciosCliente = async (
  client: SupabaseClient<Database>,
  clienteId: string,
  base: ServicioLeido[],
  final: ServicioAlta[],
): Promise<ResultadoGuardarServicios> => {
  const { error } = await (client.rpc as unknown as RpcGuardarServicios)('guardar_servicios_cliente', {
    p_cliente_id: clienteId,
    p_base: base.map(({ id, servicio_id, precio, frecuencia, mes_inicio }) => ({
      id,
      servicio_id,
      precio,
      frecuencia,
      mes_inicio,
    })),
    p_final: final,
  })
  if (!error) return { conflicto: false }
  if (error.code === CODIGO_CONFLICTO_SERVICIOS) {
    return { conflicto: true, servicioIds: servicioIdsDelDetalle(error.details) }
  }
  throw error
}
